import React, { useEffect, useState } from 'react';

import DataTableContainer from '../../components/DataTableContainer';
import MyDataTable from '../../components/MyDataTable';
import { NoDvrStreamServer, ServerFarmInterface, StreamServer, streamServersService, TimelineProvider, VideoStorageProvider } from '../../services/central-api/stream-servers';
import { translate } from '../../services/translate';
import { View } from 'react-native';
import Badge from '../../components/Badge';
import DropdownButton from '../../components/DropdownButton';
import DataTableHeader from '../../components/DataTableHeader';
import MyDropDownPicker from '../../components/MyDropDownPicker';
import moment from 'moment';
import PurgeCamerasModal from './PurgeCamerasModal';
import { RootNavigation } from '../../Routes';
import { InfoInRow } from '../../components/InfoInRow';
import UpdateTimelineProviderModal from './UpdateTimelineProviderModal';
import UpdateVideoStorageProviderModal from './UpdateVideoStorageProviderModal';
import AlertModal from '../../components/AlertModal';
import { TranslateRich } from '../../components/TranslateRich';

interface ServerFarmDetailServersParams {
    serverFarm: ServerFarmInterface;
    setServerPoolId: React.Dispatch<React.SetStateAction<string | null>>;
    serverPoolId: string | null;
    setTimelineProviderId: React.Dispatch<React.SetStateAction<number | null>>;
    timelineProviderId: number | null;
    setVideoStorageProviderId: React.Dispatch<React.SetStateAction<number | null>>;
    videoStorageProviderId: number | null;
    refetch: () => void;
    rootNavigation: RootNavigation;
}

export default function ServerFarmDetailServers(params: ServerFarmDetailServersParams) {
    const [disableModalVisible, setDisableModalVisible] = useState(false);
    const [enableModalVisible, setEnableModalVisible] = useState(false);
    const [restartModalVisible, setRestartModalVisible] = useState(false);
    const [markAsDeletedModalVisible, setMarkAsDeletedModalVisible] = useState(false);
    const [purgeCamerasModalVisible, setPurgeCamerasModalVisible] = useState(false);
    const [timelineProviderModalVisible, setTimelineProviderModalVisible] = useState(false);
    const [videoStorageProviderModalVisible, setVideoStorageProviderModalVisible] = useState(false);

    const [selectedServer, setSelectedServer] = useState<NoDvrStreamServer & { type: 'no-dvr'; } | StreamServer & { type: 'default'; } | null>(null);
    const [filteredServers, setFilteredServers] = useState<(NoDvrStreamServer & { type: 'no-dvr'; } | StreamServer & { type: 'default'; })[]>([]);

    const [timelineProviders, setTimelineProviders] = useState<{ [timelineProviderId: number]: TimelineProvider | undefined; }>([]);
    const [videoStorageProviders, setVideoStorageProviders] = useState<{ [videoStorageProviderId: number]: VideoStorageProvider | undefined; }>([]);

    const [serverPoolDropdownOpen, setServerPoolDropdownOpen] = useState(false);
    const [videoStorageProviderDropdownOpen, setVideoStorageProviderDropdownOpen] = useState(false);
    const [timelineProviderDropdownOpen, setTimelineProviderDropdownOpen] = useState(false);

    const [availableServerPools, setAvailableServerPools] = useState<{ id: string; }[]>([]);
    const [availableTimelineProviders, setAvailableTimelineProviders] = useState<{ id: number; name: string; }[]>([]);
    const [availableVideoStorageProviders, setAvailableVideoStorageProviders] = useState<{ id: number; name: string; }[]>([]);

    useEffect(() => {
        setFilteredServers(([
            ...params.serverFarm.servers.map(s => ({ ...s, type: 'default' })),
            ...params.serverFarm.noDvrServers.map(s => ({ ...s, type: 'no-dvr' })),
        ] as (NoDvrStreamServer & { type: 'no-dvr'; } | StreamServer & { type: 'default'; })[]).filter((server) => {
            if (params.serverPoolId && server.serverPoolId !== params.serverPoolId) {
                return false;
            }
            if (params.timelineProviderId && (server.type !== 'default' || server.timelineProviderId !== params.timelineProviderId)) {
                return false;
            }
            if (params.videoStorageProviderId && (server.type !== 'default' || server.videoStorageProviderId !== params.videoStorageProviderId)) {
                return false;
            }
            return true;
        }));
    }, [params.serverPoolId, params.timelineProviderId, params.videoStorageProviderId, params.serverFarm]);

    useEffect(() => {
        if (!params.serverFarm) {
            return;
        }
        setTimelineProviders(params.serverFarm.timelineProviders.reduce((previousValue, currentValue) => {
            previousValue[currentValue.id] = currentValue;
            return previousValue;
        }, {} as { [timelineProviderId: number]: TimelineProvider; }));
        setVideoStorageProviders(params.serverFarm.videoStorageProviders.reduce((previousValue, currentValue) => {
            previousValue[currentValue.id] = currentValue;
            return previousValue;
        }, {} as { [videoStorageProviderId: number]: VideoStorageProvider; }));
    }, [params.serverFarm]);

    function renderServerContent(server: NoDvrStreamServer & { type: 'no-dvr'; } | StreamServer & { type: 'default'; }) {
        return <View style={{ paddingVertical: 10, paddingLeft: 50, flexDirection: 'column', rowGap: 5 }}>
            <InfoInRow title={'domain'} value={server.domain} />
            <InfoInRow title={'createdAt'} value={moment(server.createdAt).format('DD/MM/YYYY HH:mm')} />
            <InfoInRow title={'serverPool'} value={server.serverPoolId} />
            {server.type === 'default' && server.serverPoolToPurgeCamerasId && <InfoInRow title={'purgingTo'} value={server.serverPoolToPurgeCamerasId} />}
            {server.internalAddress && <InfoInRow title={'internalAddress'} value={server.internalAddress} />}
            {server.bareMetalMachine && <>
                <InfoInRow title={'publicIpv4'} value={server.bareMetalMachine.ipv4Address} />
                <InfoInRow title={'publicIpv6'} value={server.bareMetalMachine.ipv6Address} />
            </>}
            {server.type === 'default' && <InfoInRow title={'useGPFileTransfer'} value={server.useGPFileTransfer} />}
        </View>;
    }

    return (<View style={{ flex: 1 }}>
        <DataTableHeader
            title={`${filteredServers.length} ${translate('servers')} - ${filteredServers.reduce((previousValue, currentValue) =>
                previousValue + (currentValue.type === 'default' ? currentValue.cameraCount : 0), 0)} ${translate('cameras')}`}
            zIndex={1}
            filter={<View style={{
                flex: 1,
                flexDirection: 'row',
                gap: 5,
            }}>
                <View>
                    <MyDropDownPicker
                        placeholder={translate('serverPools')}
                        searchable={true}
                        items={[{}, ...params.serverFarm.serverPools.map(sp => ({
                            value: sp.id,
                            label: sp.id,
                        }))]}
                        open={serverPoolDropdownOpen}
                        setOpen={setServerPoolDropdownOpen}
                        setValue={params.setServerPoolId}
                        value={params.serverPoolId}
                        zIndex={3}
                    />
                </View>
                <View>
                    <MyDropDownPicker
                        placeholder={translate('timelineProviders')}
                        searchable={true}
                        items={[{}, ...params.serverFarm.timelineProviders.map(tp => ({
                            value: tp.id,
                            label: `${tp.id} - ${timelineProviders[tp.id]?.timelineType || ''} - ${moment(timelineProviders[tp.id]?.createdAt || '').format('DD/MM/YYYY')}`,
                        }))]}
                        open={timelineProviderDropdownOpen}
                        setOpen={setTimelineProviderDropdownOpen}
                        setValue={params.setTimelineProviderId}
                        value={params.timelineProviderId}
                        zIndex={2}
                    />
                </View>
                <View>
                    <MyDropDownPicker
                        placeholder={translate('videoStorageProviders')}
                        searchable={true}
                        items={[{}, ...params.serverFarm.videoStorageProviders.map(vsp => ({
                            value: vsp.id,
                            label: `${vsp.id} - ${videoStorageProviders[vsp.id]?.blobStorageType || ''} - ${moment(videoStorageProviders[vsp.id]?.createdAt || '').format('DD/MM/YYYY')}`,
                        }))]}
                        open={videoStorageProviderDropdownOpen}
                        setOpen={setVideoStorageProviderDropdownOpen}
                        setValue={params.setVideoStorageProviderId}
                        value={params.videoStorageProviderId}
                        zIndex={1}
                    />
                </View>
            </View>}
        />
        <DataTableContainer
            dataTable={<MyDataTable
                data={filteredServers}
                expandableRowsComponent={({ data }) => renderServerContent(data)}
                expandableRows
                pointerOnHover
                onRowClicked={(row) => {
                    params.rootNavigation.navigate('CRM', {
                        screen: 'Management', params: {
                            page: 0,
                            limit: 25,
                            serverId: row.id,
                        }
                    });
                }}
                columns={[{
                    name: translate('id'),
                    cell: row => row.id,
                    minWidth: '75px',
                    grow: 0,
                }, {
                    name: translate('domain'),
                    selector: row => row.domain,
                }, {
                    name: translate('status'),
                    cell: row => {
                        return <View style={{ justifyContent: 'center', alignItems: 'center' }}>
                            {row.state === 'enabled' && <Badge mode='SQUARE' color='green'>{row.state}</Badge>}
                            {row.state === 'manual_disabled' &&
                                <Badge mode='SQUARE' color={row.type === 'default' && row.serverPoolToPurgeCamerasId && row.cameraCount > 0 ? 'purple' : 'orange'}>
                                    {row.type === 'default' && row.serverPoolToPurgeCamerasId && row.cameraCount > 0 ? 'purging' : row.state}
                                </Badge>
                            }
                            {row.state === 'waiting_for_deletion' && <Badge mode='SQUARE' color='red'>{row.state}</Badge>}
                            {row.state === 'waiting_server_config' && <Badge mode='SQUARE' color='yellow'>{row.state}</Badge>}
                        </View>;
                    },
                    minWidth: '170px',
                    grow: 0,
                }, {
                    name: translate('cameras'),
                    cell: row => row.type === 'default' ? row.cameraCount : '',
                    minWidth: '100px',
                    grow: 0,
                    center: true,
                }, {
                    name: translate('timelineProvider'),
                    selector: row => {
                        return row.type === 'default' ? `${row.timelineProviderId} - ${timelineProviders[row.timelineProviderId]?.timelineType || ''} - ${moment(timelineProviders[row.timelineProviderId]?.createdAt || '').format('DD/MM/YYYY')}` : '';
                    },
                }, {
                    name: translate('videoStorageProvider'),
                    selector: row => {
                        return row.type === 'default' ? `${row.videoStorageProviderId} - ${videoStorageProviders[row.videoStorageProviderId]?.blobStorageType || ''} - ${moment(videoStorageProviders[row.videoStorageProviderId]?.createdAt || '').format('DD/MM/YYYY')}` : '';
                    },
                }, {
                    name: '',
                    button: true,
                    wrap: true,
                    cell: (row) => {
                        return (
                            <DropdownButton
                                size={320}
                                offsetX={-168}
                                actions={[{
                                    content: translate('changeTimelineProvider'),
                                    omit: !['enabled', 'manual_disabled'].includes(row.state) || row.type !== 'default',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setTimelineProviderModalVisible(true);

                                        const serverServerPool = params.serverFarm.serverPools.find(sp => sp.id === row.serverPoolId);

                                        if (!serverServerPool) {
                                            return setAvailableTimelineProviders([]);
                                        }

                                        const timelineProviders = params.serverFarm.timelineProviders.map(tp => {
                                            return {
                                                id: tp.id,
                                                name: `${tp.id} - ${tp.timelineType} - ${moment(tp.createdAt).format('DD/MM/YYYY')}`
                                            };
                                        });
                                        setAvailableTimelineProviders(timelineProviders);
                                    },
                                }, {
                                    content: translate('changeVideoStorageProvider'),
                                    omit: !['enabled', 'manual_disabled'].includes(row.state) || row.type !== 'default',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setVideoStorageProviderModalVisible(true);

                                        const serverServerPool = params.serverFarm.serverPools.find(sp => sp.id === row.serverPoolId);

                                        if (!serverServerPool) {
                                            return setAvailableTimelineProviders([]);
                                        }

                                        const providers = params.serverFarm.videoStorageProviders.map(p => {
                                            return {
                                                id: p.id,
                                                name: `${p.id} - ${p.blobStorageType || ''} - ${moment(p.createdAt || '').format('DD/MM/YYYY')}`
                                            };
                                        });
                                        setAvailableVideoStorageProviders(providers);
                                    },
                                }, {
                                    omit: !(row.type !== 'default' || (row.state === 'manual_disabled' && row.serverPoolToPurgeCamerasId === null)),
                                    content: translate('purgeCameras'),
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setPurgeCamerasModalVisible(true);

                                        const serverServerPool = params.serverFarm.serverPools.find(sp => sp.id === row.serverPoolId);

                                        if (!serverServerPool) {
                                            return setAvailableServerPools([]);
                                        }

                                        const serverPools = params.serverFarm.serverPools.filter(sp => sp.type === serverServerPool.type);

                                        setAvailableServerPools(serverPools);
                                    },
                                }, {
                                    omit: row.state !== 'enabled',
                                    content: translate('disable'),
                                    variant: 'warning',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setDisableModalVisible(true);
                                    },
                                }, {
                                    omit: row.state !== 'manual_disabled',
                                    content: translate('enable'),
                                    variant: 'success',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setEnableModalVisible(true);
                                    },
                                }, {
                                    omit: !['enabled', 'manual_disabled'].includes(row.state),
                                    content: translate('restart'),
                                    variant: 'warning',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setRestartModalVisible(true);
                                    },
                                }, {
                                    omit: !((row.type === 'default' && row.state === 'manual_disabled' && row.cameraCount === 0)
                                        || (row.type === 'no-dvr' && row.state === 'manual_disabled')
                                    ),
                                    content: translate('markAsDeleted'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedServer(row);
                                        setMarkAsDeletedModalVisible(true);
                                    },
                                }]}
                            />
                        );
                    }
                }]}
            />}
        />
        <AlertModal
            setModalVisible={setEnableModalVisible}
            isModalVisible={enableModalVisible}
            title={translate('attention')}
            type='warning'
            customButtonLabel={translate('confirm')}
            description={<>
                <TranslateRich
                    i18nKey={'enableServer'}
                    values={{
                        server: <strong>{`${selectedServer?.id} - ${selectedServer?.domain}`}</strong>,
                    }}
                />
            </>}
            onSubmit={async () => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    if (selectedServer.type === 'default') {
                        await streamServersService.enableServer(selectedServer.id);
                    }
                    if (selectedServer.type === 'no-dvr') {
                        await streamServersService.enableNoDvrServer(selectedServer.id);
                    }
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
        />
        <AlertModal
            setModalVisible={setRestartModalVisible}
            isModalVisible={restartModalVisible}
            title={translate('attention')}
            type='warning'
            customButtonLabel={translate('confirm')}
            description={<>
                <TranslateRich
                    i18nKey={'restartServer'}
                    values={{
                        server: <strong>{`${selectedServer?.id} - ${selectedServer?.domain}`}</strong>,
                    }}
                />
            </>}
            onSubmit={async () => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    if (selectedServer.type === 'default') {
                        await streamServersService.restartServer(selectedServer.id);
                    }
                    if (selectedServer.type === 'no-dvr') {
                        await streamServersService.restartNoDvrServer(selectedServer.id);
                    }
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
        />
        <AlertModal
            setModalVisible={setDisableModalVisible}
            isModalVisible={disableModalVisible}
            title={translate('attention')}
            type='warning'
            customButtonLabel={translate('confirm')}
            description={<>
                <TranslateRich
                    i18nKey={'disableServer'}
                    values={{
                        server: <strong>{`${selectedServer?.id} - ${selectedServer?.domain}`}</strong>,
                    }}
                />
            </>}
            onSubmit={async () => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    if (selectedServer.type === 'default') {
                        await streamServersService.disableServer(selectedServer.id);
                    }
                    if (selectedServer.type === 'no-dvr') {
                        await streamServersService.disableNoDvrServer(selectedServer.id);
                    }
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
        />
        <AlertModal
            setModalVisible={setMarkAsDeletedModalVisible}
            isModalVisible={markAsDeletedModalVisible}
            title={translate('attention')}
            type='destructive'
            customButtonLabel={translate('confirm')}
            description={<>
                <TranslateRich
                    i18nKey={'markServerAsDeleted'}
                    values={{
                        server: <strong>{`${selectedServer?.id} - ${selectedServer?.domain}`}</strong>,
                    }}
                />
            </>}
            onSubmit={async () => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    if (selectedServer.type === 'default') {
                        await streamServersService.markServerToBeDeleted(selectedServer.id);
                    }
                    if (selectedServer.type === 'no-dvr') {
                        await streamServersService.markNoDvrServerToBeDeleted(selectedServer.id);
                    }
                    params.refetch();
                } catch (err) {
                    console.error(err);
                }
            }}
        />
        <PurgeCamerasModal
            setModalVisible={setPurgeCamerasModalVisible}
            isModalVisible={purgeCamerasModalVisible}
            availableServerPools={availableServerPools}
            onSubmit={async (serverPoolId) => {
                try {
                    if (!selectedServer) {
                        return;
                    }
                    await streamServersService.purgeServerCamerasToServerPool(selectedServer.id, serverPoolId);
                    params.refetch();
                    setSelectedServer(null);
                } catch (err) {
                    console.error(err);
                }
            }}
        />

        {selectedServer &&
            <UpdateTimelineProviderModal
                setModalVisible={setTimelineProviderModalVisible}
                isModalVisible={timelineProviderModalVisible}
                availableTimelineProviders={availableTimelineProviders}
                onSubmit={async (timelineProviderId) => {
                    try {
                        await streamServersService.updateTimelineProvider(timelineProviderId, selectedServer.id);
                        params.refetch();
                        setSelectedServer(null);
                    } catch (err) {
                        console.error(err);
                    }
                }}
            />
        }

        {selectedServer &&
            <UpdateVideoStorageProviderModal
                setModalVisible={setVideoStorageProviderModalVisible}
                isModalVisible={videoStorageProviderModalVisible}
                availableVideoStorageProviders={availableVideoStorageProviders}
                onSubmit={async (videoStorageProviderId, useGPFileTransfer) => {
                    try {
                        await streamServersService.updateVideoStorageProvider(videoStorageProviderId, selectedServer.id, useGPFileTransfer);
                        params.refetch();
                        setSelectedServer(null);
                        setVideoStorageProviderModalVisible(false);
                    } catch (err) {
                        console.error(err);
                    }
                }}
            />
        }

    </View>);
}
